import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Loader2, ShieldCheck, Smartphone, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { authLogin, authVerify2fa, authSetupVerify, formatApiError } from "@/lib/api";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [stage, setStage] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [setupInfo, setSetupInfo] = useState(null);

  const submitCreds = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authLogin(email, password);
      setPending(res.pending_token);
      if (res.stage === "setup") {
        setSetupInfo({ qr: res.qr, secret: res.secret });
        setStage("setup");
      } else {
        setStage("totp");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("6 haneli kodu girin."); return; }
    setBusy(true);
    try {
      const fn = stage === "setup" ? authSetupVerify : authVerify2fa;
      const res = await fn(pending, code);
      login(res.token, res.user);
      toast.success("Giriş başarılı.");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setCode("");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative z-[1]">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-11 w-11 rounded-md bg-[var(--pos)] flex items-center justify-center">
            <Bot className="h-6 w-6 text-black" strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-head font-black text-xl tracking-tight">CRYPTOSENSE</div>
            <div className="text-[10px] text-[var(--accent)] tracking-[0.25em]">AI TRADING DESK</div>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-7">
          {stage === "credentials" && (
            <form onSubmit={submitCreds} className="space-y-5">
              <div>
                <h1 className="font-head font-bold text-2xl">Giriş Yap</h1>
                <p className="text-sm text-[var(--text-dim)] mt-1">Hesabınıza güvenli erişim</p>
              </div>
              <div>
                <Label className="text-xs text-[var(--text-dim)]">E-posta</Label>
                <Input data-testid="login-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required
                  className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11" />
              </div>
              <div>
                <Label className="text-xs text-[var(--text-dim)]">Şifre</Label>
                <Input data-testid="login-password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11" />
              </div>
              <Button data-testid="login-submit" type="submit" disabled={busy}
                className="w-full h-12 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Devam Et"}
              </Button>
              <p className="text-[11px] text-[var(--text-faint)] text-center">
                Hesabınız yok mu?{" "}
                <button type="button" data-testid="go-register" onClick={() => nav("/register")}
                  className="text-[var(--pos)] hover:underline underline-offset-2">Kayıt olun</button>
                {" · "}
                <button type="button" data-testid="go-forgot" onClick={() => nav("/forgot-password")}
                  className="text-[var(--accent)] hover:underline underline-offset-2">Şifremi unuttum</button>
              </p>
            </form>
          )}

          {stage === "setup" && (
            <form onSubmit={submitCode} className="space-y-5">
              <div className="flex items-center gap-2 text-[var(--accent)]">
                <Smartphone className="h-5 w-5" />
                <h1 className="font-head font-bold text-xl">Authenticator Kurulumu</h1>
              </div>
              <p className="text-sm text-[var(--text-dim)]">
                Google Authenticator / Authy ile aşağıdaki QR kodu tarayın, ardından üretilen 6 haneli kodu girin.
              </p>
              {setupInfo?.qr && (
                <div className="flex flex-col items-center gap-3">
                  <img data-testid="totp-qr" src={setupInfo.qr} alt="QR" className="h-44 w-44 rounded-lg bg-white p-2" />
                  <div className="text-center">
                    <div className="text-[10px] text-[var(--text-faint)] uppercase tracking-wider">Manuel Anahtar</div>
                    <code className="text-xs text-[var(--pos)] break-all">{setupInfo.secret}</code>
                  </div>
                </div>
              )}
              <CodeInput code={code} setCode={setCode} />
              <Button data-testid="totp-submit" type="submit" disabled={busy}
                className="w-full h-12 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1.5" /> Kurulumu Tamamla</>}
              </Button>
            </form>
          )}

          {stage === "totp" && (
            <form onSubmit={submitCode} className="space-y-5">
              <button type="button" onClick={() => setStage("credentials")}
                className="flex items-center gap-1 text-xs text-[var(--text-faint)] hover:text-[var(--text-primary)]">
                <ArrowLeft className="h-3 w-3" /> Geri
              </button>
              <div className="flex items-center gap-2 text-[var(--accent)]">
                <ShieldCheck className="h-5 w-5" />
                <h1 className="font-head font-bold text-xl">İki Adımlı Doğrulama</h1>
              </div>
              <p className="text-sm text-[var(--text-dim)]">Authenticator uygulamanızdaki 6 haneli kodu girin.</p>
              <CodeInput code={code} setCode={setCode} />
              <Button data-testid="totp-submit" type="submit" disabled={busy}
                className="w-full h-12 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Doğrula & Giriş Yap"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const CodeInput = ({ code, setCode }) => (
  <div>
    <Label className="text-xs text-[var(--text-dim)]">Doğrulama Kodu</Label>
    <Input
      data-testid="totp-code"
      value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
      inputMode="numeric"
      placeholder="000000"
      autoFocus
      className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-center text-2xl tracking-[0.5em] h-14"
    />
  </div>
);
